import PizZip from 'pizzip'
import Docxtemplater from 'docxtemplater'

/**
 * Opções de configuração para renderização de template DOCX.
 */
export interface DocxRendererOptions {
    /**
     * Função chamada para tags sem valor correspondente nos dados.
     * Retorna a string a inserir no documento. Por padrão retorna string vazia,
     * evitando erros em formulários com campos opcionais.
     */
    nullGetter?: (part: { value: string }, scopeManager?: unknown) => string
    /**
     * Parser personalizado para pré-processar valores antes da substituição.
     * Útil para transformar tipos como `Date` em strings formatadas ou booleanos em `"Sim"/"Não"`.
     * @example
     * // Formata datas automaticamente para pt-BR
     * parser: (tag) => ({
     *   get: (scope) => {
     *     const val = scope[tag]
     *     if (val instanceof Date) return val.toLocaleDateString('pt-BR')
     *     return val ?? ''
     *   }
     * })
     */
    parser?: (tag: string) => { get: (scope: Record<string, unknown>) => unknown }
}

/**
 * Provider para renderização de documentos Word (.docx) a partir de templates.
 * Utiliza PizZip e Docxtemplater internamente. Não deve ser instanciada.
 * @example
 * const blob = DocxRenderer.render(templateBuffer, { nome: 'João', data: new Date() })
 */
export abstract class DocxRenderer {
    /**
     * Renderiza um template DOCX substituindo as tags pelos valores fornecidos.
     * Tags sem valor correspondente retornam string vazia por padrão (via `nullGetter`).
     * Erros de renderização do Docxtemplater são propagados com detalhes de cada tag inválida.
     * @param template Template DOCX como `ArrayBuffer`. Para converter de `Blob`: `await blob.arrayBuffer()`.
     * @param data Objeto com os dados para substituição das tags no template.
     * @param options Opções adicionais de renderização (`nullGetter`, `parser`).
     * @returns `Blob` do documento Word renderizado no formato `.docx`.
     * @throws {Error} Se a renderização falhar ou o template for inválido.
     */
    static render(template: ArrayBuffer, data: Record<string, unknown>, options?: DocxRendererOptions): Blob {
        try {
            return new Docxtemplater(new PizZip(template), {
                paragraphLoop: true,
                linebreaks: true,
                nullGetter: options?.nullGetter ?? (() => ''),
                ...(options?.parser ? { parser: options.parser } : {}),
            })
                .render(data)
                .getZip()
                .generate({
                    type: 'blob',
                    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    compression: 'DEFLATE',
                })
        } catch (err: unknown) {
            const detail = extractDocxtemplaterErrors(err)
            const base = detail ?? (err instanceof Error ? err.message : String(err))
            throw new Error(`Erro na renderização do documento: ${base}`, { cause: err })
        }
    }
}

function extractDocxtemplaterErrors(err: unknown): string | null {
    if (err !== null && typeof err === 'object' && 'properties' in err) {
        const props = (err as { properties?: { errors?: Array<{ message?: string }> } }).properties
        if (props?.errors?.length) {
            return props.errors.map((e) => e.message ?? String(e)).join('; ')
        }
    }
    return null
}
